import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthContext";
import { api, type VerdictOnlyConfig } from "@/lib/api/client";
import { NUM_UNREAD_LOGS_PATH } from "@/lib/api/clients/UserLogClient";
import {
  deriveStatus,
  lastAnsweredAt,
  useConnectionStore,
} from "@/lib/connection/connectionStore";

/** Green/busy: probe when nothing has answered for this long. */
export const HEARTBEAT_IDLE_MS = 60_000;
/** Unreachable: probe this often until something answers. */
export const PROBE_UNREACHABLE_MS = 10_000;
/** One ticker for both cadences. */
export const TICK_MS = 5_000;
/** A burst of `online` events sends one confirmation probe, not one each. */
export const ONLINE_PROBE_THROTTLE_MS = 2_000;
/**
 * The probe's own deadline. `api` deliberately has NO timeout (a hanging
 * write is allowed to hold amber for as long as it takes), so without this a
 * black-holed origin — TCP accepted, no bytes — would keep the dot green
 * until the gateway gives up on our behalf (ALB 504 ≈ 60 s, Cloudflare 524
 * at 100 s). Past the deadline axios throws ECONNABORTED, which `client.ts`
 * reads as "no answer" ⇒ red ⇒ the 10 s recovery cadence. Trade-off: a
 * server that needs longer than this for its cheapest GET paints red until
 * the next answer from anywhere.
 */
export const PROBE_TIMEOUT_MS = 15_000;

/**
 * The app-bar query. It throws on any failure and runs with `retry: false`
 * (a failed poll keeps the last good bar painted, status "error"), so the red
 * strip outlives the outage until a window-focus refetch — the watcher
 * re-fetches it on recovery instead, and
 * ONLY then: a healthy bar is left alone, or every blip would cost the whole
 * five-request bundle again and a flapping link would chain refetches.
 */
const APP_BAR_QUERY_KEY = ["initialiseUserBase"];

/**
 * The cheapest authenticated GET we have (`/api/v1/user-logs/num-unread`,
 * no rate limit, no Mongo aggregate) — the bell's URL, but through `api.get`
 * directly rather than `numUnreadLogs`, because the shim's `apiGet` takes
 * headers only and the probe needs its own `timeout`. Its RESULT is
 * irrelevant: the axios interceptors in `client.ts` turn the answer — or its
 * absence — into the connection verdict. The request throws on failure, and
 * an unhandled rejection would reach Sentry, so the promise is swallowed
 * here on purpose. The settled promise is returned so the caller can keep
 * one probe in flight at a time.
 *
 * Verdict-only: the interceptor casts the verdict and rethrows, without the
 * token reissue (a hung reissue would pin the in-flight guard for good) and
 * without the `mfaRequired` hard-navigation. So the heartbeat never refreshes
 * an expired access token — the tab's next real request does — and its 401
 * still counts as "answered".
 */
function probe(): Promise<unknown> {
  const config: VerdictOnlyConfig = {
    headers: { "Cache-Control": "no-store" },
    timeout: PROBE_TIMEOUT_MS,
    __verdictOnly: true,
  };
  return api.get(NUM_UNREAD_LOGS_PATH, config).catch(() => {});
}

/**
 * Mounted once at the root (inside `AuthProvider`). Feeds the connection
 * store with the browser's own online/offline signal and keeps a sparse
 * heartbeat, because the app bar polls NOTHING on its own: without it an
 * idle tab would stay green through an outage and red after one.
 *
 * The heartbeat only runs while the tab is visible, the browser online and a
 * user signed in; on pages with their own pollers (guest list 45 s,
 * seating 30 s) the 60 s idle rule never fires.
 *
 * "Browser online" has one deliberate exception: an offline verdict that the
 * tick derived from `navigator.onLine` (not from a real `offline` event) keeps
 * the recovery probe. On a machine whose flag is wrongly false (VPN / virtual
 * adapters — the case `noteServerAnswered` overrules) no `online` event will
 * ever come, so the probe is the only way back to green.
 */
export function ConnectionWatcher(): null {
  const { state } = useAuth();
  const authenticated = state.kind === "authenticated";
  const queryClient = useQueryClient();
  const lastProbeRef = useRef(0);
  /** A probe is in flight. Its deadline (15 s) outlives the red cadence
   *  (10 s), so without this a black-holed origin would get a second probe
   *  on top of the first. */
  const probeInFlightRef = useRef(false);
  /** True while the current `browserOnline === false` came from the tick's
   *  `navigator.onLine` fallback rather than from an `offline` event. */
  const fallbackOfflineRef = useRef(false);

  useEffect(() => {
    const store = useConnectionStore;

    const canProbe = () =>
      authenticated &&
      (store.getState().browserOnline || fallbackOfflineRef.current) &&
      typeof document !== "undefined" &&
      document.visibilityState === "visible";

    const probeNow = () => {
      if (probeInFlightRef.current) return;
      probeInFlightRef.current = true;
      lastProbeRef.current = Date.now();
      void probe().finally(() => {
        probeInFlightRef.current = false;
      });
    };

    // The strip is up: the last poll failed (status "error", with or without a
    // painted bar — the query throws), or a legacy cached ServerError VALUE.
    const appBarBroken = () => {
      if (queryClient.getQueryState(APP_BAR_QUERY_KEY)?.status === "error") return true;
      const data = queryClient.getQueryData<unknown>(APP_BAR_QUERY_KEY);
      return (
        typeof data === "object" &&
        data !== null &&
        (data as { isServerError?: unknown }).isServerError === true
      );
    };

    // Red, or the fallback's offline (a real offline never passes `canProbe`).
    const needsRecovery = () => {
      const status = deriveStatus(store.getState());
      return status === "unreachable" || (status === "offline" && fallbackOfflineRef.current);
    };

    const probeIfUnreachable = () => {
      if (needsRecovery() && canProbe()) probeNow();
    };

    const tick = () => {
      // The store trusts a server answer over `navigator.onLine` (some VPNs
      // report offline while everything works). Once there is NO fresh
      // evidence — the last request got no answer — fall back to the
      // browser's flag, so a response that slipped in after a real `offline`
      // event cannot leave the dot saying "server" when it should say "offline".
      {
        const s = store.getState();
        if (
          typeof navigator !== "undefined" &&
          navigator.onLine === false &&
          s.browserOnline &&
          !s.reachable
        ) {
          fallbackOfflineRef.current = true;
          s.setBrowserOnline(false);
        }
      }
      if (!canProbe()) return;
      const now = Date.now();
      if (needsRecovery()) {
        if (now - lastProbeRef.current >= PROBE_UNREACHABLE_MS) probeNow();
        return;
      }
      // live / busy: a slow heartbeat, but only when nothing else is talking
      // to the server. A probe that is still in flight counts as "talking".
      const lastSignal = Math.max(lastAnsweredAt() ?? 0, lastProbeRef.current);
      if (now - lastSignal >= HEARTBEAT_IDLE_MS) probeNow();
    };

    // Order matters: the flag must flip BEFORE the probe gate looks at it.
    // Then ONE confirmation probe regardless of the verdict: with nothing
    // stale and nothing in flight, react-query sends nothing on reconnect,
    // and the dot would sit on an unverified green until the 60 s heartbeat.
    const onOnline = () => {
      store.getState().setBrowserOnline(true);
      // Some browsers fire `online` several times on one network switch.
      if (canProbe() && Date.now() - lastProbeRef.current > ONLINE_PROBE_THROTTLE_MS) {
        probeNow();
      }
    };
    // A real `offline` is not the fallback: probing stops until `online`.
    const onOffline = () => {
      fallbackOfflineRef.current = false;
      store.getState().setBrowserOnline(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") probeIfUnreachable();
    };

    // The store captured `navigator.onLine` at import time; re-sync in case
    // the flag flipped between module evaluation and this mount.
    if (typeof navigator !== "undefined" && typeof navigator.onLine === "boolean") {
      store.getState().setBrowserOnline(navigator.onLine);
    }

    const unsubscribe = store.subscribe((s, prev) => {
      // Evidence (`noteServerAnswered`) or an `online` event ended the
      // fallback's offline; the next one has to be derived afresh.
      if (s.browserOnline) fallbackOfflineRef.current = false;
      // Once per outage, on the way back only — never on mount, never on a
      // flag-only flip, never on repeated `true`, never for a healthy bar.
      if (s.reachable && !prev.reachable && appBarBroken()) {
        void queryClient.invalidateQueries({ queryKey: APP_BAR_QUERY_KEY });
      }
    });
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(tick, TICK_MS);

    return () => {
      unsubscribe();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [authenticated, queryClient]);

  return null;
}
